import { useQuery } from "@tanstack/react-query";
import { CalendarDays, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import type { MeDTO, WindowDTO, WindowSummaryDTO } from "../../shared/dto.js";
import { inRange } from "../../shared/glucose.js";
import { nextDelayMs } from "../../shared/refresh.js";
import { timelineRows } from "../../shared/timeline.js";
import { api, ApiError } from "@/api";
import { GlucoseChart } from "@/charts/GlucoseChart";
import { RangeBar, RangeLegend } from "@/charts/RangeBar";
import { Timeline } from "@/components/Timeline";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/cn";
import { addDays, ago, arrow, carbs, dayTitle, delta, insulin, isDateString, longDate, mgdl, percent, todayIn } from "@/lib/format";
import { useNow } from "@/lib/useNow";

export function DayPage({ me }: { me: MeDTO }) {
  const params = useParams();
  const navigate = useNavigate();
  const now = useNow(30_000);
  const today = todayIn(me.zone, now);
  const date = params.date ?? today;
  const isToday = date === today;
  // A share link with "last N days" shows nothing before them.
  const earliest = me.share?.lastDays ? addDays(today, -(me.share.lastDays - 1)) : null;
  const isFirst = earliest !== null && date <= earliest;
  const valid = isDateString(date) && date <= today && (earliest === null || date >= earliest);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [highlightT, setHighlightT] = useState<number | null>(null);
  useEffect(() => {
    setSelectedKey(null);
    setHighlightT(null);
  }, [date]);

  const query = useQuery({
    queryKey: ["window", date],
    queryFn: () => api.window({ date }),
    staleTime: isToday ? 0 : 5 * 60_000,
    refetchInterval: (q) => (isToday ? nextDelayMs(q.state.data?.latest?.t ?? null, Date.now()) : false),
    enabled: valid,
  });
  const data = query.data;
  const rows = useMemo(() => (data ? timelineRows(data) : []), [data]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft" && !isFirst) navigate(`/day/${addDays(date, -1)}`);
      else if (e.key === "ArrowRight" && !isToday) navigate(`/day/${addDays(date, 1)}`);
      else if (e.key === "t") navigate("/day");
      else if (e.key === "Escape") setSelectedKey(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [date, isToday, isFirst, navigate]);

  if (!valid) return <Navigate to="/day" replace />;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-1 sm:gap-2">
        <Button variant="ghost" size="icon" aria-label="Previous day" title="Previous day (←)" disabled={isFirst} onClick={() => navigate(`/day/${addDays(date, -1)}`)}>
          <ChevronLeft />
        </Button>
        <div className="min-w-40 text-center">
          <h1 className="text-xl font-semibold">{dayTitle(date, today)}</h1>
          <p className="text-xs text-muted">{longDate(date, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
        </div>
        <Button variant="ghost" size="icon" aria-label="Next day" title="Next day (→)" disabled={isToday} onClick={() => navigate(`/day/${addDays(date, 1)}`)}>
          <ChevronRight />
        </Button>
        <DatePicker value={date} min={earliest ?? undefined} max={today} onChange={(d) => navigate(d === today ? "/day" : `/day/${d}`)} />
        {!isToday && (
          <Button size="sm" onClick={() => navigate("/day")} title="Today (T)">
            Today
          </Button>
        )}
        <div className="ml-auto flex items-center gap-3">
          {isToday && data?.latest && <Latest latest={data.latest} now={now} />}
          {me.role === "owner" && !me.preview && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Reload from Nightscout"
              title="Reload from Nightscout"
              onClick={() => api.window({ date, refresh: true }).then(() => query.refetch())}
            >
              <RefreshCw className={cn(query.isFetching && "animate-spin")} />
            </Button>
          )}
        </div>
      </header>

      {query.isError && <ErrorCard error={query.error} />}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-4">
          <Card className="p-2 sm:p-4">
            {data ? (
              <GlucoseChart data={data} nowMs={isToday ? now : undefined} selectedKey={selectedKey} highlightT={highlightT} onSelect={setSelectedKey} />
            ) : (
              <div className="grid h-[380px] place-items-center text-sm text-muted">{query.isPending ? "Loading…" : "No data"}</div>
            )}
          </Card>
          {data && <DayStats summary={data.summary} isToday={isToday} />}
        </div>

        <Card className="flex flex-col xl:sticky xl:top-[4.5rem] xl:max-h-[calc(100vh-5.5rem)]">
          <h2 className="border-b border-line px-4 py-3 text-sm font-medium">Timeline</h2>
          <Timeline
            rows={rows}
            zone={me.zone}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
            onHover={setHighlightT}
            onOpen={(row) => {
              const id = row.kind === "entry" ? row.entry.id : row.kind === "note" ? row.note.id : null;
              if (id) navigate(`/entry/${id}`);
            }}
            className="min-h-0 flex-1 overflow-y-auto"
            empty={data ? "Nothing logged this day." : "…"}
          />
        </Card>
      </div>
    </div>
  );
}

function DatePicker({ value, min, max, onChange }: { value: string; min?: string; max: string; onChange: (date: string) => void }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <span className="relative">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Pick a date"
        title="Pick a date"
        onClick={() => {
          const el = input.current;
          if (!el) return;
          try {
            el.showPicker();
          } catch {
            el.focus();
          }
        }}
      >
        <CalendarDays />
      </Button>
      <input
        ref={input}
        type="date"
        value={value}
        min={min}
        max={max}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0"
        onChange={(e) => {
          if (isDateString(e.target.value) && e.target.value <= max && (!min || e.target.value >= min)) onChange(e.target.value);
        }}
      />
    </span>
  );
}

function Latest({ latest, now }: { latest: NonNullable<WindowDTO["latest"]>; now: number }) {
  const stale = now - latest.t > 15 * 60_000;
  return (
    <div className="flex items-baseline gap-2" title={stale ? "No recent reading" : undefined}>
      <span
        className="text-3xl font-semibold tabular-nums"
        style={{ color: stale ? "var(--text-muted)" : inRange(latest.mgdl) ? "var(--chart-in-range)" : "var(--chart-out-of-range)" }}
      >
        {latest.mgdl}
      </span>
      <span className="text-2xl">{arrow(latest.direction)}</span>
      <span className="text-sm text-muted tabular-nums">
        mg/dL{latest.delta !== null && <>&nbsp;&nbsp;{delta(latest.delta)}</>} · {ago(latest.t, now)}
      </span>
    </div>
  );
}

function DayStats({ summary, isToday }: { summary: WindowSummaryDTO; isToday: boolean }) {
  const m = summary.metrics;
  return (
    <Card className="p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-sm font-medium">{isToday ? "Today so far" : "This day"}</h2>
        <span className="text-xs text-muted">
          {m.readings} readings · {percent(m.coverage)} of the time covered
        </span>
      </div>

      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
        <div className="shrink-0">
          <div className="text-4xl font-semibold tabular-nums" style={{ color: "var(--band-in-range)" }}>
            {percent(m.bands?.inRange ?? null)}
          </div>
          <div className="text-xs text-muted">in range 70–180</div>
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <RangeBar bands={m.bands} />
          <RangeLegend bands={m.bands} />
        </div>
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-4 border-t border-line pt-4 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Mean" value={mgdl(m.mean)} unit="mg/dL" />
        <Stat label="Std. deviation" value={mgdl(m.sd)} unit="mg/dL" />
        <Stat label="Variability (CV)" value={m.cv === null ? "–" : `${m.cv.toFixed(1)}%`} hint="goal ≤ 36%" warn={m.cv !== null && m.cv > 36} />
        <Stat label="Tight range" value={percent(m.tight)} hint="70–140" />
        <Stat label="Carbs (pump)" value={carbs(summary.carbs)} hint={plural(summary.carbEntries, "entry", "entries")} />
        <Stat label="Bolus insulin" value={insulin(summary.bolusInsulin)} hint={plural(summary.boluses, "bolus", "boluses")} />
      </dl>
    </Card>
  );
}

function Stat({ label, value, unit, hint, warn }: { label: string; value: string; unit?: string; hint?: string; warn?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5">
        <span className={cn("text-lg font-semibold tabular-nums", warn && "text-danger")}>{value}</span>
        {unit && <span className="ml-1 text-xs text-muted">{unit}</span>}
        {hint && <div className="text-xs text-muted">{hint}</div>}
      </dd>
    </div>
  );
}

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

function ErrorCard({ error }: { error: Error }) {
  const message = error instanceof ApiError ? error.message : "Couldn't load this day.";
  return (
    <Card className="border-danger/40 px-4 py-3 text-sm text-danger" role="alert">
      {message}
    </Card>
  );
}
